import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  bg:      "#08101e",
  bg1:     "#0c1525",
  bg2:     "#101b2e",
  bg3:     "#141f33",
  border:  "#1e2d42",
  border2: "#243548",
  text:    "#e8edf5",
  body:    "#b8c8dc",
  meta:    "#6a7d95",
  dim:     "#2d3f58",
  green:   "#00e896",
  cyan:    "#00c8ff",
  amber:   "#ffb340",
  red:     "#ff4466",
  blue:    "#4488ff",
  purple:  "#9966ff",
  gDim:    "rgba(0,232,150,0.07)",
  cDim:    "rgba(0,200,255,0.07)",
  aDim:    "rgba(255,179,64,0.07)",
  pDim:    "rgba(153,102,255,0.07)",
};

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────
const LAST_SESSION = { date:"Feb 26", trades:10, winners:8, losers:2, pnl:"+$1,658", rate:80 };

const DAILY = [
  { date:"Feb 26", trades:10, winners:8, losers:2, pnl:"+$1,658", rate:80, roi:83,
    scenario:"TRENDING", scenarioColor:"#00e896",
    scenarioNote:"Strong directional day · bot found 8 high-confidence setups · all stops held on losses" },
  { date:"Feb 25", trades:1, winners:1, losers:0, pnl:"+$20", rate:100, roi:1,
    scenario:"SELECTIVE", scenarioColor:"#00c8ff",
    scenarioNote:"Choppy regime · bot evaluated 9 setups · skipped 8 · 1 high-conviction entry only" },
  { date:"Feb 24", trades:9, winners:5, losers:4, pnl:"-$140", rate:56, roi:-7,
    scenario:"DEFENSIVE", scenarioColor:"#ff4466",
    scenarioNote:"Volatile open · 4 stops triggered as designed · max loss per trade enforced · no runaway positions" },
  { date:"Feb 23", trades:12, winners:10, losers:2, pnl:"+$720", rate:83, roi:36,
    scenario:"RECOVERY", scenarioColor:"#9966ff",
    scenarioNote:"Session after defensive day · no behavioral change · bot executed normal scan · 83% win rate" },
];

const LAYERS = [
  { n:"01", name:"PREDICT",  icon:"◎", color:C.cyan,
    title:"Domain Forecasting",
    desc:"Three models trained exclusively on SPY data forecast price 10, 20 and 60 minutes ahead. Not generic signals — a proprietary understanding of SPY microstructure built from years of tick data. Each horizon feeds a different part of the decision engine." },
  { n:"02", name:"VALIDATE", icon:"⊕", color:C.purple,
    title:"Reinforcement Learning Cross-Check",
    desc:"An RL model trained inside a simulated trade environment cross-checks every signal. It has played thousands of virtual trades and learned to recognise the specific conditions that historically precede losses — a disciplined risk filter before capital is ever deployed." },
  { n:"03", name:"DECIDE",   icon:"⬡", color:C.green,
    title:"Agent Decision Engine",
    desc:"The agent combines both signals with live market context and makes the final call — PROCEED or SKIP. It sets the target and stop loss, selects the optimal options strike, and defines the position timeframe. Fully autonomous. Fully explainable." },
  { n:"04", name:"MONITOR",  icon:"◈", color:C.amber,
    title:"Reactive Position Management",
    desc:"Every 5 minutes the monitoring agent re-evaluates the open position. HOLD, ADJUST the stop or target, or CLOSE if the original thesis has collapsed. It never gets tired, never gets emotional, and never holds a losing position hoping the market turns." },
  { n:"05", name:"LEARN",    icon:"◑", color:C.blue,
    title:"Adaptive Memory Architecture",
    desc:"Short-term memory captures recent session patterns and regime behaviour. Long-term memory accumulates knowledge across hundreds of trades. The system compounds its intelligence with every closed position — getting smarter alongside the capital it manages." },
];

const MOAT = [
  { icon:"◎", color:C.cyan,   title:"Proprietary training corpus",
    desc:"The SPY models were trained on a specific historical dataset assembled over time. A competitor starting today needs the same data, the same architecture decisions, and months of iteration to reach comparable signal quality." },
  { icon:"⊕", color:C.purple, title:"RL simulator depth",
    desc:"The reinforcement learning validator has processed thousands of virtual trades in a simulated environment. That simulation fidelity is not replicable quickly — it represents accumulated training time that cannot be shortcut." },
  { icon:"◑", color:C.blue,   title:"Memory compounds daily",
    desc:"Every real session adds labeled trade outcomes to long-term memory. The system's pattern recognition improves with every closed position. The longer it runs, the harder it is to match — the moat widens with time, not just with capital." },
  { icon:"⬡", color:C.green,  title:"Infinite liquidity at scale",
    desc:"SPY 0DTE has effectively unlimited liquidity at current position sizes. Scaling from 10 contracts to 100 moves no markets and degrades no signal. The strategy does not break at scale — a rare property most retail algo strategies cannot claim." },
];

const PHASES = [
  { phase:"NOW",    label:"SPY 0DTE",           status:"live",    color:C.green,
    desc:"Model trained. Signal proven. Daily capital cycle validated at 78% win rate across recent sessions." },
  { phase:"NEXT",   label:"QQQ + SPY Expansion", status:"ready",   color:C.cyan,
    desc:"High SPY correlation means the architecture transfers with incremental fine-tuning. QQQ has the liquidity depth required at scale." },
  { phase:"SCALE",  label:"AAPL · TSLA · NVDA",  status:"planned", color:C.purple,
    desc:"Deep 0DTE liquidity on major equities. Each instrument gets its own trained model on the same reusable pipeline." },
  { phase:"EXPAND", label:"ES · NQ Futures",      status:"planned", color:C.amber,
    desc:"No PDT rules. Opens the platform to sub-$25k accounts. An entirely new market segment with better capital efficiency." },
];

const COST_ROWS = [
  { label:"Infrastructure",  them:"$2–5M / year †",        us:"< $15 / month",          color:C.cyan   },
  { label:"Team",            them:"10–20 PhD quants",       us:"5 autonomous AI layers", color:C.purple },
  { label:"Decision logic",  them:"Black box, unexplained", us:"Transparent, auditable", color:C.green  },
  { label:"Access barrier",  them:"Accredited / invite",    us:"$25k PDT minimum",       color:C.amber  },
  { label:"Training compute",them:"Data centre GPU farm",   us:"Consumer gaming desktop",color:C.blue   },
];

const CAT_COLS = [
  { key:"signal", label:"Signal\nGenerators", eg:"TradingView\nTrade Ideas" },
  { key:"copy",   label:"Copy\nTrading",       eg:"eToro\nCollective2"       },
  { key:"robo",   label:"Robo-\nAdvisors",     eg:"Betterment\nWealthfront"  },
  { key:"edu",    label:"Education\nPlatforms", eg:"Warrior\nTrading"        },
  { key:"broker", label:"Pro Broker\nTools",    eg:"tastytrade\nthinkorswim" },
  { key:"berbul", label:"BerBul",               eg:"Autonomous\nAgent", isBerBul:true },
];

const CAT_ROWS = [
  { group:"THE HUMAN PROBLEM", rows:[
    { label:"Human still executes?", sub:"Does a person decide entry/exit?",
      signal:{v:"✓ Always",c:"neg"}, copy:{v:"◑ Follows human",c:"part"}, robo:{v:"— Rules-based",c:"neg"},
      edu:{v:"✓ That's the point",c:"neg"}, broker:{v:"✓ Your call",c:"neg"},
      berbul:{v:"— Agent executes",c:"pos",note:"Every entry, exit, adjustment"} },
    { label:"Removes emotional execution?", sub:"Fear, hope, revenge trading eliminated",
      signal:{v:"— No",c:"neg"}, copy:{v:"— No",c:"neg"}, robo:{v:"◑ Partial",c:"part"},
      edu:{v:"— Teaches discipline",c:"neg"}, broker:{v:"— No",c:"neg"},
      berbul:{v:"✓ Structural",c:"pos",note:"Not willpower — the system can't override itself"} },
  ]},
  { group:"DECISION INTELLIGENCE", rows:[
    { label:"Genuine AI decision-making", sub:"Beyond rules and indicators",
      signal:{v:"— Signals only",c:"neg"}, copy:{v:"— Human strategy",c:"neg"}, robo:{v:"— Allocation rules",c:"neg"},
      edu:{v:"— None",c:"neg"}, broker:{v:"— Analytics only",c:"neg"},
      berbul:{v:"✓ 5-layer AI",c:"pos",note:"Predict · Validate · Decide · Monitor · Learn"} },
    { label:"Adapts intraday to conditions", sub:"Re-evaluates while in position",
      signal:{v:"— Static alerts",c:"neg"}, copy:{v:"◑ Depends",c:"part"}, robo:{v:"— No",c:"neg"},
      edu:{v:"— No",c:"neg"}, broker:{v:"— You adapt",c:"neg"},
      berbul:{v:"✓ Every 5 min",c:"pos",note:"HOLD / ADJUST / CLOSE on live conditions"} },
    { label:"Learns from every session", sub:"Intelligence compounds over time",
      signal:{v:"— No",c:"neg"}, copy:{v:"— You learn",c:"neg"}, robo:{v:"— No",c:"neg"},
      edu:{v:"◑ You learn",c:"part"}, broker:{v:"— No",c:"neg"},
      berbul:{v:"✓ Memory layers",c:"pos",note:"Short + long-term memory, session to session"} },
  ]},
  { group:"TRANSPARENCY", rows:[
    { label:"Every decision explained?", sub:"Plain-English reasoning per trade",
      signal:{v:"— Signal only",c:"neg"}, copy:{v:"— Black box",c:"neg"}, robo:{v:"— No",c:"neg"},
      edu:{v:"◑ Teaches why",c:"part"}, broker:{v:"— Shows P&L",c:"neg"},
      berbul:{v:"✓ Glass box",c:"pos",note:"Every PROCEED/SKIP logged with full reasoning"} },
  ]},
  { group:"RISK & CAPITAL", rows:[
    { label:"Defined max daily exposure", sub:"Structural cap — not manual discipline",
      signal:{v:"— You size it",c:"neg"}, copy:{v:"◑ Depends",c:"part"}, robo:{v:"◑ Allocation %",c:"part"},
      edu:{v:"— You manage risk",c:"neg"}, broker:{v:"— Your call",c:"neg"},
      berbul:{v:"✓ < $4k always",c:"pos",note:"~15% of account, enforced by the system"} },
    { label:"Zero overnight risk", sub:"All positions closed by 4pm",
      signal:{v:"— Your choice",c:"neg"}, copy:{v:"— Depends",c:"neg"}, robo:{v:"— Holds positions",c:"neg"},
      edu:{v:"— Your choice",c:"neg"}, broker:{v:"— Your choice",c:"neg"},
      berbul:{v:"✓ Enforced",c:"pos",note:"No exceptions. Capital recycles daily."} },
  ]},
  { group:"WHAT YOU NEED", rows:[
    { label:"Trading skill required", sub:"To use it effectively",
      signal:{v:"High",c:"neg"}, copy:{v:"Moderate",c:"part"}, robo:{v:"Low",c:"pos"},
      edu:{v:"That's the product",c:"neg"}, broker:{v:"High",c:"neg"},
      berbul:{v:"Low",c:"pos",note:"Monitor results, not markets"} },
    { label:"Time per day", sub:"Active screen time needed",
      signal:{v:"4–8 hrs",c:"neg"}, copy:{v:"1–2 hrs",c:"part"}, robo:{v:"~5 min",c:"pos"},
      edu:{v:"4–8 hrs",c:"neg"}, broker:{v:"4–8 hrs",c:"neg"},
      berbul:{v:"~5 min",c:"pos",note:"Check before 9:30 · review after 4pm"} },
  ]},
];

const TICKS = [
  { label:"WIN RATE",          val:"78%",          color:C.green  },
  { label:"AVG HOLD TIME",     val:"97 seconds",   color:C.cyan   },
  { label:"AVG DAILY P&L",     val:"+$487",        color:C.green  },
  { label:"MAX LIVE EXPOSURE", val:"< $4,000",     color:C.amber  },
  { label:"INSTRUMENT",        val:"SPY 0DTE",     color:C.cyan   },
  { label:"STRATEGY",          val:"SCALP",        color:C.purple },
  { label:"PROFITABLE DAYS",   val:"18 of 22",     color:C.green  },
  { label:"OVERNIGHT RISK",    val:"ZERO",         color:C.red    },
  { label:"AI LAYERS",         val:"5 — AGENTIC",  color:C.blue   },
  { label:"TOTAL RETURN",      val:"+$14,280",     color:C.green  },
];

const VC_STATS = [
  { num:"78",  suffix:"%",    label:"Win Rate",         sub:"recent paper sessions",  color:C.green  },
  { num:"487", prefix:"$",   label:"Avg Daily Return", sub:"fixed capital deployed", color:C.cyan   },
  { num:"47",                label:"Sessions Run",      sub:"since Nov 2024",         color:C.purple },
  { num:"20",  prefix:"$", suffix:"/mo", label:"Infra Cost", sub:"total monthly",    color:C.amber  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MARKET HOURS HELPER
// ─────────────────────────────────────────────────────────────────────────────
function getMarketStatus() {
  const now = new Date();
  const et  = new Date(now.toLocaleString("en-US", { timeZone:"America/New_York" }));
  const day = et.getDay();
  const mins = et.getHours() * 60 + et.getMinutes();
  const isWeekday = day >= 1 && day <= 5;
  const isOpen    = isWeekday && mins >= 570 && mins < 960;
  const timeStr   = et.toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit", timeZoneName:"short" });
  return { isOpen, timeStr };
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────────────────────
function useInView(ref, threshold = 0.08) {
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } },
      { threshold }
    );
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return v;
}

function useCountUp(end, active, ms = 1800) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const num = parseFloat(String(end).replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return;
    let s = null;
    const tick = t => {
      if (!s) s = t;
      const p = Math.min((t - s) / ms, 1);
      setN(Math.round((1 - Math.pow(1 - p, 3)) * num));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, end]);
  return n;
}

// ─────────────────────────────────────────────────────────────────────────────
// MICRO COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function Orb({ color, size = 7, speed = "3s" }) {
  return (
    <span style={{ position:"relative", display:"inline-block", width:size, height:size, flexShrink:0 }}>
      <span style={{ position:"absolute", inset:0, borderRadius:"50%", background:color, opacity:.12,
        animation:`orbPulse ${speed} ease-in-out infinite` }} />
      <span style={{ position:"absolute", inset:Math.round(size * .28), borderRadius:"50%", background:color }} />
    </span>
  );
}

function Chip({ children, color = C.cyan }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", padding:"3px 10px", borderRadius:3,
      fontSize:9, fontFamily:"'DM Mono',monospace", fontWeight:500, letterSpacing:".8px",
      whiteSpace:"nowrap", background:color + "12", border:`1px solid ${color}28`, color }}>
      {children}
    </span>
  );
}

function Eyebrow({ children }) {
  return (
    <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.meta,
      letterSpacing:"2px", marginBottom:16, textTransform:"uppercase" }}>
      {children}
    </div>
  );
}

function Heading({ children, style = {} }) {
  return (
    <h2 style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
      fontSize:"clamp(22px,2.8vw,34px)", letterSpacing:"-.3px",
      lineHeight:1.22, color:C.text, margin:"0 0 20px", ...style }}>
      {children}
    </h2>
  );
}

function Body({ children, style = {} }) {
  return (
    <p style={{ fontFamily:"'DM Mono',monospace", fontSize:12,
      color:C.body, lineHeight:1.92, fontWeight:300, margin:0, ...style }}>
      {children}
    </p>
  );
}

function Divider() {
  return <div style={{ height:1, background:C.border, margin:"110px 0" }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// MARKET BAR
// ─────────────────────────────────────────────────────────────────────────────
function MarketBar() {
  const [status, setStatus] = useState(getMarketStatus());
  useEffect(() => {
    const t = setInterval(() => setStatus(getMarketStatus()), 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ background:C.bg1, borderBottom:`1px solid ${C.border}`,
      padding:"8px 52px", display:"flex", alignItems:"center",
      justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <Orb color={status.isOpen ? C.green : C.meta} size={7} speed={status.isOpen ? "2s" : "4s"} />
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:status.isOpen ? C.green : C.meta, letterSpacing:"1px", fontWeight:500 }}>
          {status.isOpen ? "MARKET OPEN" : "MARKET CLOSED"}
        </span>
        <span style={{ color:C.dim, fontSize:9 }}>·</span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.meta, letterSpacing:".5px" }}>
          {status.timeStr}
        </span>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:16 }}>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.meta }}>
          Example session {LAST_SESSION.date}
        </span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.green, fontWeight:500 }}>
          {LAST_SESSION.pnl}
        </span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.body }}>
          {LAST_SESSION.trades} trades · {LAST_SESSION.rate}% win rate
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TICKER
// ─────────────────────────────────────────────────────────────────────────────
function Ticker() {
  const all = [...TICKS, ...TICKS];
  return (
    <div style={{ overflow:"hidden", background:C.bg, borderTop:`1px solid ${C.border}`,
      borderBottom:`1px solid ${C.border}`, position:"relative" }}>
      {["left","right"].map(s => (
        <div key={s} style={{ position:"absolute", [s]:0, top:0, bottom:0, width:80,
          background:`linear-gradient(${s === "left" ? "90" : "270"}deg, ${C.bg}, transparent)`,
          zIndex:2, pointerEvents:"none" }} />
      ))}
      <div style={{ display:"flex", width:"max-content", animation:"ticker 44s linear infinite" }}>
        {all.map((t, i) => (
          <div key={i} style={{ display:"flex", flexDirection:"column", justifyContent:"center",
            gap:5, padding:"14px 32px", borderRight:`1px solid ${C.border}`, minWidth:152 }}>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:8, color:C.meta, letterSpacing:"1.2px", lineHeight:1 }}>
              {t.label}
            </span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:16, fontWeight:500, color:t.color, lineHeight:1 }}>
              {t.val}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({ num, label, sub, color, prefix = "", suffix = "", delay = 0 }) {
  const ref = useRef();
  const vis = useInView(ref);
  const raw = parseFloat(String(num).replace(/[^0-9.]/g, ""));
  const counted = useCountUp(raw, vis);
  const display = !isNaN(raw) ? `${prefix}${counted.toLocaleString()}${suffix}` : num;
  return (
    <div ref={ref} style={{ background:C.bg1, border:`1px solid ${C.border}`, borderRadius:12,
      padding:"32px 28px", opacity:vis ? 1 : 0,
      animation:vis ? `fadeUp .6s ${delay}s ease both` : "none" }}>
      <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
        fontSize:"clamp(26px,2.8vw,40px)", color:color || C.text,
        letterSpacing:"-.5px", lineHeight:1, marginBottom:14 }}>
        {display}
      </div>
      <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.meta,
        letterSpacing:"1.2px", marginBottom:sub ? 7 : 0, textTransform:"uppercase" }}>
        {label}
      </div>
      {sub && (
        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:C.body }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PERFORMANCE TABLE
// ─────────────────────────────────────────────────────────────────────────────
function PerfTable() {
  const ref = useRef();
  const vis = useInView(ref);
  return (
    <div ref={ref} style={{ opacity:vis ? 1 : 0, animation:vis ? "fadeUp .7s ease both" : "none" }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:14, padding:"16px 20px",
        marginBottom:10, background:"rgba(255,179,64,0.06)", border:`1px solid ${C.amber}28`, borderRadius:10 }}>
        <span style={{ fontSize:14, color:C.amber, flexShrink:0, paddingTop:1 }}>⊙</span>
        <div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.amber,
            letterSpacing:"1.2px", fontWeight:500, marginBottom:5 }}>
            CURATED EXAMPLE · PAPER TRADING · NOV 2024–FEB 2026
          </div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:C.body, lineHeight:1.7, fontWeight:300 }}>
            Four real paper-trading sessions selected to show how the system behaves across different
            market conditions — including a loss day and a quiet day. Not filtered for wins.
            Full session history available after login.
          </div>
        </div>
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>
        {[
          { label:"TRENDING",  color:C.green,  desc:"High-conviction day" },
          { label:"SELECTIVE", color:C.cyan,   desc:"Bot traded sparingly" },
          { label:"DEFENSIVE", color:C.red,    desc:"Loss day · stops held" },
          { label:"RECOVERY",  color:C.purple, desc:"Session after loss" },
        ].map(s => (
          <div key={s.label} style={{ display:"flex", alignItems:"center", gap:7, padding:"5px 11px",
            background:s.color + "0e", border:`1px solid ${s.color}28`, borderRadius:4 }}>
            <span style={{ width:5, height:5, borderRadius:"50%", background:s.color, flexShrink:0 }} />
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:8, color:s.color,
              letterSpacing:".8px", fontWeight:500 }}>{s.label}</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:8, color:C.meta }}>· {s.desc}</span>
          </div>
        ))}
      </div>
      <div style={{ background:C.bg1, border:`1px solid ${C.border}`, borderRadius:14, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:"'DM Mono',monospace" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                {["DATE","SESSION TYPE","TRADES","W / L","NET P&L","WIN RATE","SESSION ROI"].map(h => (
                  <th key={h} style={{ padding:"12px 20px", fontSize:9, color:C.meta,
                    letterSpacing:"1.1px", textAlign:"left", fontWeight:400, whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAILY.map((d, i) => {
                const isLoss = d.pnl.startsWith("-");
                const pnlColor = isLoss ? C.red : C.green;
                const roiColor = isLoss ? C.red : C.purple;
                return (
                  <tr key={i}
                    style={{ borderBottom:i < DAILY.length-1 ? `1px solid ${C.bg2}` : "none",
                      transition:"background .15s", cursor:"default",
                      borderLeft:`2px solid ${d.scenarioColor}40` }}
                    onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding:"16px 20px", color:C.body, fontSize:11, whiteSpace:"nowrap" }}>{d.date}</td>
                    <td style={{ padding:"16px 20px", minWidth:220 }}>
                      <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                        <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:3,
                          fontSize:8, fontWeight:500, letterSpacing:".8px", color:d.scenarioColor,
                          background:d.scenarioColor + "12", border:`1px solid ${d.scenarioColor}30`,
                          width:"fit-content" }}>{d.scenario}</span>
                        <span style={{ fontSize:9, color:C.meta, lineHeight:1.5, fontWeight:300 }}>
                          {d.scenarioNote}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding:"16px 20px", color:C.text, fontSize:11 }}>{d.trades}</td>
                    <td style={{ padding:"16px 20px", fontSize:11 }}>
                      <span style={{ color:C.green }}>{d.winners}</span>
                      <span style={{ color:C.meta }}> / </span>
                      <span style={{ color:d.losers > 0 ? C.red : C.meta }}>{d.losers}</span>
                    </td>
                    <td style={{ padding:"16px 20px", color:pnlColor, fontWeight:500, fontSize:15 }}>{d.pnl}</td>
                    <td style={{ padding:"16px 20px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:48, height:2, background:C.bg3, borderRadius:1, flexShrink:0 }}>
                          <div style={{ height:"100%", borderRadius:1,
                            background:d.rate >= 70 ? C.green : d.rate >= 55 ? C.amber : C.red,
                            width:`${d.rate}%` }} />
                        </div>
                        <span style={{ color:d.rate >= 70 ? C.green : d.rate >= 55 ? C.amber : C.red,
                          fontSize:11, fontWeight:500 }}>{d.rate}%</span>
                      </div>
                    </td>
                    <td style={{ padding:"16px 20px", color:roiColor, fontWeight:500, fontSize:13 }}>
                      {isLoss ? "" : "+"}{d.roi}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop:`1px solid ${C.border}`, background:C.bg }}>
                <td colSpan={7} style={{ padding:"12px 20px", fontSize:9, color:C.meta, lineHeight:1.7 }}>
                  ∗ Paper account · SPY 0DTE options · curated example sessions ·
                  past performance does not guarantee future results
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ARCHITECTURE
// ─────────────────────────────────────────────────────────────────────────────
function Architecture() {
  const ref = useRef();
  const vis = useInView(ref);
  return (
    <div ref={ref}>
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {LAYERS.map((l, i) => (
          <div key={i} style={{ display:"flex", gap:24, alignItems:"flex-start",
            padding:"28px 32px", borderRadius:12, background:C.bg1,
            border:`1px solid ${C.border}`, transition:"border-color .25s, background .25s",
            opacity:vis ? 1 : 0, animation:vis ? `fadeUp .55s ${i * .1}s ease both` : "none" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = l.color + "35"; e.currentTarget.style.background = C.bg2; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.bg1; }}>
            <div style={{ flexShrink:0, display:"flex", flexDirection:"column",
              alignItems:"center", gap:8, paddingTop:3, minWidth:44 }}>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
                color:l.color, fontWeight:500, letterSpacing:".5px" }}>{l.n}</span>
              <span style={{ fontSize:22, color:l.color, lineHeight:1 }}>{l.icon}</span>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:12 }}>
                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
                  fontWeight:500, color:l.color, letterSpacing:"1px" }}>{l.name}</span>
                <span style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
                  fontSize:14, color:C.text }}>{l.title}</span>
              </div>
              <p style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:C.body,
                lineHeight:1.9, fontWeight:300, margin:"0 0 14px", maxWidth:680 }}>{l.desc}</p>
              <Chip color={l.color}>{l.name} · LAYER {l.n}</Chip>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flexWrap:"wrap",
        gap:0, padding:"28px 32px", background:C.bg, borderRadius:10,
        border:`1px solid ${C.border}`, marginTop:10 }}>
        {LAYERS.map((l, i) => (
          <React.Fragment key={i}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, padding:"0 4px" }}>
              <span style={{ fontSize:18, color:l.color }}>{l.icon}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
                color:l.color, letterSpacing:"1px", fontWeight:500 }}>{l.name}</span>
            </div>
            {i < LAYERS.length - 1 && (
              <span style={{ color:C.dim, fontSize:14, padding:"0 18px", marginBottom:6 }}>→</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MOAT SECTION
// ─────────────────────────────────────────────────────────────────────────────
function MoatSection() {
  const ref = useRef();
  const vis = useInView(ref);
  return (
    <div ref={ref} style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:12 }}>
      {MOAT.map((m, i) => (
        <div key={i} style={{ background:C.bg1, border:`1px solid ${C.border}`,
          borderRadius:12, padding:"28px 28px",
          borderTopColor:m.color + "38", borderTopWidth:2,
          opacity:vis ? 1 : 0, animation:vis ? `fadeUp .55s ${i * .09}s ease both` : "none" }}>
          <div style={{ fontSize:20, color:m.color, marginBottom:14 }}>{m.icon}</div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
            fontSize:13, color:C.text, marginBottom:10 }}>{m.title}</div>
          <p style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
            color:C.body, lineHeight:1.88, fontWeight:300, margin:0 }}>{m.desc}</p>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROADMAP
// ─────────────────────────────────────────────────────────────────────────────
function Roadmap() {
  const ref = useRef();
  const vis = useInView(ref);
  return (
    <div ref={ref} style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {PHASES.map((p, i) => (
        <div key={i} style={{ display:"flex", gap:24, alignItems:"flex-start",
          padding:"24px 28px", background:C.bg1,
          border:`1px solid ${p.status === "live" ? p.color + "28" : C.border}`,
          borderRadius:12,
          opacity:vis ? 1 : 0, animation:vis ? `fadeUp .55s ${i * .09}s ease both` : "none" }}>
          <div style={{ flexShrink:0, width:72, paddingTop:2 }}>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:8, fontWeight:500,
              color:p.color, letterSpacing:".8px", padding:"3px 7px",
              border:`1px solid ${p.color}30`, borderRadius:3,
              display:"inline-block", marginBottom:6 }}>
              {{ live:"LIVE", ready:"READY", planned:"PLANNED" }[p.status]}
            </div>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
              color:C.meta, letterSpacing:".5px" }}>{p.phase}</div>
          </div>
          <div>
            <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
              fontSize:14, color:C.text, marginBottom:8 }}>{p.label}</div>
            <p style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
              color:C.body, lineHeight:1.82, fontWeight:300, margin:0 }}>{p.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COST STRUCTURE
// ─────────────────────────────────────────────────────────────────────────────
function CostStructure() {
  const ref = useRef();
  const vis = useInView(ref);
  return (
    <div ref={ref} style={{ opacity:vis ? 1 : 0, animation:vis ? "fadeUp .7s ease both" : "none" }}>
      <div style={{ background:C.bg1, border:`1px solid ${C.border}`,
        borderRadius:14, overflow:"hidden", marginBottom:10 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr",
          borderBottom:`1px solid ${C.border}`, padding:"14px 28px" }}>
          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.meta, letterSpacing:"1.1px" }}></span>
          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.meta, letterSpacing:"1.1px" }}>
            TRADITIONAL QUANT FUND
          </span>
          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.cyan, letterSpacing:"1.1px" }}>
            BERBUL
          </span>
        </div>
        {COST_ROWS.map((r, i) => (
          <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr",
            padding:"18px 28px",
            borderBottom:i < COST_ROWS.length - 1 ? `1px solid ${C.bg2}` : "none",
            transition:"background .15s" }}
            onMouseEnter={e => e.currentTarget.style.background = C.bg2}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:C.meta }}>{r.label}</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:C.body, fontWeight:300 }}>{r.them}</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:r.color, fontWeight:500 }}>{r.us}</span>
          </div>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginTop:12 }}>
        {[
          { label:"MONTHLY CLOUD COST",   val:"< $15",  sub:"total infrastructure",  color:C.green  },
          { label:"MONTHLY LLM COST",     val:"~$5",    sub:"all inference included", color:C.cyan   },
          { label:"TRAINING HARDWARE",    val:"$1,500", sub:"one-time · 7yr old GPU", color:C.purple },
        ].map(({ label, val, sub, color }) => (
          <div key={label} style={{ background:C.bg1, border:`1px solid ${C.border}`,
            borderRadius:12, padding:"24px 24px" }}>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
              color:C.meta, letterSpacing:"1.1px", marginBottom:10 }}>{label}</div>
            <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
              fontSize:28, color, letterSpacing:"-.3px", marginBottom:6 }}>{val}</div>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
              color:C.body, fontWeight:300 }}>{sub}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:12, padding:"14px 20px", background:C.bg,
        border:`1px solid ${C.border}`, borderRadius:8 }}>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
          color:C.meta, letterSpacing:".4px", lineHeight:1.8 }}>
          † Industry infrastructure estimates, Tabb Group / Burton-Taylor research.
          BerBul does not claim signal quality equivalence to institutional funds —
          the architecture follows the same principles. Actual performance will differ.
          Current costs reflect the paper trading phase — compute scales proportionally
          with revenue as live accounts grow.
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORY COMPARISON
// ─────────────────────────────────────────────────────────────────────────────
function CategoryComparison() {
  const ref = useRef();
  const vis = useInView(ref);
  const colW = ["220px","130px","130px","130px","130px","130px","160px"];
  const cellColor = (c) => c === "pos" ? C.green : c === "part" ? C.amber : C.meta;

  return (
    <div ref={ref} style={{ opacity:vis ? 1 : 0, animation:vis ? "fadeUp .7s ease both" : "none",
      overflowX:"auto" }}>
      <div style={{ minWidth:1020 }}>
        <div style={{ display:"grid", gridTemplateColumns:colW.join(" "),
          borderBottom:`1px solid ${C.border}` }}>
          <div style={{ padding:"0 0 14px" }} />
          {CAT_COLS.map(col => (
            <div key={col.key} style={{ padding:"0 16px 14px",
              borderLeft:col.isBerBul ? `1px solid rgba(0,200,255,0.3)` : `1px solid ${C.border}`,
              background:col.isBerBul ? "rgba(0,200,255,0.04)" : "transparent" }}>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9, letterSpacing:"1px",
                color:col.isBerBul ? C.cyan : C.meta, whiteSpace:"pre", lineHeight:1.5,
                fontWeight:col.isBerBul ? 500 : 400 }}>{col.label}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:8, color:C.meta,
                marginTop:3, opacity:0.6, whiteSpace:"pre", lineHeight:1.4 }}>{col.eg}</div>
            </div>
          ))}
        </div>

        {CAT_ROWS.map((group) => (
          <div key={group.group}>
            <div style={{ display:"grid", gridTemplateColumns:colW.join(" ") }}>
              <div style={{ padding:"20px 0 8px", fontFamily:"'DM Mono',monospace",
                fontSize:8, letterSpacing:"1.4px", color:C.meta, opacity:0.7,
                gridColumn:"1 / -1", borderBottom:`1px solid ${C.border}` }}>{group.group}</div>
            </div>
            {group.rows.map((row, ri) => (
              <div key={ri} style={{ display:"grid", gridTemplateColumns:colW.join(" "),
                borderBottom:`1px solid ${C.bg2}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.bg1}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div style={{ padding:"14px 16px 14px 0" }}>
                  <div style={{ fontSize:12, color:C.body, fontWeight:500, marginBottom:2 }}>{row.label}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.meta, lineHeight:1.4 }}>
                    {row.sub}
                  </div>
                </div>
                {CAT_COLS.map(col => {
                  const cell = row[col.key];
                  return (
                    <div key={col.key} style={{ padding:"14px 16px",
                      borderLeft:col.isBerBul ? `1px solid rgba(0,200,255,0.2)` : `1px solid ${C.bg2}`,
                      background:col.isBerBul ? "rgba(0,200,255,0.025)" : "transparent",
                      display:"flex", flexDirection:"column", justifyContent:"center" }}>
                      <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
                        fontWeight:col.isBerBul ? 600 : 400,
                        color:cellColor(cell.c), lineHeight:1.4 }}>{cell.v}</div>
                      {cell.note && (
                        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
                          color:C.meta, marginTop:3, lineHeight:1.4, opacity:0.8 }}>{cell.note}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}

        <div style={{ display:"flex", gap:28, padding:"16px 0 0", flexWrap:"wrap" }}>
          {[
            { sym:"✓", color:C.green, label:"Present" },
            { sym:"◑", color:C.amber, label:"Partial / depends" },
            { sym:"—", color:C.meta,  label:"Not present" },
          ].map(item => (
            <div key={item.sym} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:item.color }}>{item.sym}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:C.meta, letterSpacing:".5px" }}>
                {item.label}
              </span>
            </div>
          ))}
          <div style={{ marginLeft:"auto", fontFamily:"'DM Mono',monospace", fontSize:8, color:C.meta, opacity:.5 }}>
            This is not a ranking. These platforms solve different problems.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// THESIS SECTION
// ─────────────────────────────────────────────────────────────────────────────
function ThesisSection() {
  return (
    <section style={{ padding:"80px 0 88px", borderTop:`1px solid ${C.border}`,
      borderBottom:`1px solid ${C.border}` }}>
      <div style={{ maxWidth:960, margin:"0 auto", padding:"0 40px" }}>
        <div style={{ textAlign:"center", marginBottom:52 }}>
          <h2 style={{ fontFamily:"'DM Mono',monospace", fontSize:"clamp(26px,4vw,40px)",
            fontWeight:700, lineHeight:1.15, letterSpacing:"-0.02em", margin:"0 0 6px" }}>
            <span style={{ color:C.body }}>You don't trade on BerBul.</span><br/>
            <span style={{ color:C.green }}>BerBul trades for you.</span>
          </h2>
          <p style={{ fontFamily:"'DM Mono',monospace", fontSize:14, color:C.meta,
            lineHeight:1.95, fontWeight:300, maxWidth:560, margin:"18px auto 0" }}>
            Your only job is the capital.{" "}
            <span style={{ color:C.body, fontWeight:500 }}>Fund it. Walk away.</span>
            {" "}Come back to a session log that's been right more than it's been wrong.
          </p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))",
          border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
          {[
            { num:"01", label:"the problem", title:"Human decisions destroy consistent edge",
              body:"Fear, hope, and a bad night's sleep make you trade the same setup differently every time. No amount of discipline fixes that reliably.",
              tag:"The human variable", tagBg:"rgba(255,179,64,.1)", tagColor:C.amber, tagBorder:"rgba(255,179,64,.2)", border:false },
            { num:"02", label:"the shift", title:"You are the owner. Not the operator.",
              body:"Set capital. The agent handles every entry, every monitoring check, every exit. Your job ends the moment you fund the session.",
              tag:"Fully automated", tagBg:"rgba(0,200,255,.1)", tagColor:C.cyan, tagBorder:"rgba(0,200,255,.2)", border:true },
            { num:"03", label:"the result", title:"Capital working every trading day",
              body:"In and out before 4pm. P&L realized. Capital recycled. Every decision logged in plain English. No overnight exposure. No input needed.",
              tag:"Money at work", tagBg:"rgba(0,232,150,.1)", tagColor:C.green, tagBorder:"rgba(0,232,150,.2)", border:false },
          ].map((b) => (
            <div key={b.num} style={{ background:C.bg1, padding:"26px 22px 24px",
              display:"flex", flexDirection:"column", gap:10,
              borderLeft:b.border ? `1px solid ${C.border}` : "none",
              borderRight:b.border ? `1px solid ${C.border}` : "none" }}>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:".14em",
                color:"#8aa0b8", textTransform:"uppercase", fontWeight:500 }}>{b.num} — {b.label}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:12,
                fontWeight:600, color:C.text, lineHeight:1.45 }}>{b.title}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
                color:C.meta, lineHeight:1.8, fontWeight:300, flex:1 }}>{b.body}</div>
              <span style={{ display:"inline-block", fontFamily:"'DM Mono',monospace",
                fontSize:9, letterSpacing:".1em", padding:"3px 9px", borderRadius:4,
                fontWeight:500, alignSelf:"flex-start",
                background:b.tagBg, color:b.tagColor, border:`1px solid ${b.tagBorder}` }}>{b.tag}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop:28, display:"flex", alignItems:"center", gap:8 }}>
          <Link to="/approach" style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
            color:C.cyan, textDecoration:"none", fontWeight:500 }}>
            Read the full approach →
          </Link>
          <span style={{ fontSize:9, color:C.dim }}>·</span>
          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:C.dim }}>
            No account required · paper trading by default
          </span>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RISK DISCLOSURE
// ─────────────────────────────────────────────────────────────────────────────
function RiskDisclosure() {
  return (
    <div className="bb-risk">
      <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
        color:C.meta, letterSpacing:"1.2px", marginBottom:10 }}>
        RISK DISCLOSURE
      </div>
      <p style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
        color:C.meta, lineHeight:1.85, fontWeight:300, margin:0 }}>
        Options trading involves substantial risk of loss and is not suitable for all investors.
        The performance data shown reflects paper trading sessions — simulated trades with no real
        capital at risk. Real account performance will differ. Past performance does not guarantee
        future results. The Pattern Day Trader rule requires a minimum of $25,000 account equity.
        Never deploy capital you cannot afford to lose. BerBul does not provide financial advice.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  .bb-root::before {
    content:''; position:fixed; inset:0; z-index:0; pointer-events:none;
    background-image: linear-gradient(#1e2d4222 1px, transparent 1px), linear-gradient(90deg, #1e2d4222 1px, transparent 1px);
    background-size:64px 64px;
  }
  @keyframes orbPulse { 0%,100%{transform:scale(1);opacity:.12} 50%{transform:scale(3.2);opacity:0} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ticker { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  ::-webkit-scrollbar{width:4px;height:4px}
  ::-webkit-scrollbar-track{background:#08101e}
  ::-webkit-scrollbar-thumb{background:#243548;border-radius:2px}
  input::placeholder{color:#6a7d95}
  .bb-nav {
    position:fixed;top:0;left:0;right:0;z-index:200;
    display:flex;align-items:center;justify-content:space-between;
    padding:14px 52px;background:rgba(8,16,30,0.96);
    backdrop-filter:blur(22px);border-bottom:1px solid #1e2d42;
  }
  .bb-logo{display:flex;align-items:center;gap:11px;font-family:'DM Mono',monospace;
    font-weight:500;font-size:14px;color:#e8edf5;text-decoration:none}
  .bb-mark{width:30px;height:30px;border-radius:6px;border:1px solid rgba(153,102,255,.4);
    background:rgba(153,102,255,.07);display:grid;place-items:center;
    font-family:'DM Mono',monospace;font-size:12px;color:#9966ff}
  .bb-nav-links{display:flex;gap:32px;list-style:none}
  .bb-nav-links a{font-family:'DM Mono',monospace;font-size:9px;color:#6a7d95;
    text-decoration:none;letter-spacing:1.2px;text-transform:uppercase;transition:color .2s}
  .bb-nav-links a:hover{color:#e8edf5}
  .bb-btn-ghost{padding:9px 18px;border-radius:5px;border:1px solid #243548;color:#b8c8dc;
    font-family:'DM Mono',monospace;font-size:9px;font-weight:500;letter-spacing:.6px;
    text-decoration:none;transition:border-color .2s,color .2s;background:transparent;margin-right:8px}
  .bb-btn-ghost:hover{border-color:#00c8ff;color:#00c8ff}
  .bb-btn-solid{padding:9px 18px;border-radius:5px;background:#9966ff;color:#fff;
    font-family:'DM Mono',monospace;font-size:9px;font-weight:500;letter-spacing:.6px;
    text-decoration:none;transition:opacity .2s}
  .bb-btn-solid:hover{opacity:.84}
  .bb-page{position:relative;z-index:1;max-width:1080px;margin:0 auto;padding:0 32px 100px}
  .bb-hero{padding:100px 0 90px;text-align:center;position:relative}
  .bb-glow{position:absolute;top:-80px;left:50%;transform:translateX(-50%);
    width:680px;height:400px;border-radius:50%;
    background:radial-gradient(ellipse,rgba(153,102,255,0.04) 0%,transparent 65%);pointer-events:none}
  .bb-badge{display:inline-flex;align-items:center;gap:9px;padding:6px 16px;
    border-radius:999px;border:1px solid rgba(153,102,255,.22);
    background:rgba(153,102,255,.07);font-family:'DM Mono',monospace;
    font-size:9px;color:#9966ff;letter-spacing:1.2px;margin-bottom:32px}
  .bb-h1{font-family:'DM Mono',monospace;font-weight:500;
    font-size:clamp(28px,4.8vw,56px);line-height:1.2;letter-spacing:-.5px;
    margin-bottom:12px;color:#e8edf5}
  .bb-h1-hook{display:block;font-size:clamp(14px,2vw,22px);color:#6a7d95;font-weight:300;margin-bottom:8px}
  .bb-h1-frame{display:block;font-size:clamp(14px,1.8vw,20px);color:#b8c8dc;font-weight:300;margin-top:10px}
  .bb-sub{max-width:540px;margin:28px auto 44px;font-family:'DM Mono',monospace;
    font-size:12px;line-height:1.95;color:#b8c8dc;font-weight:300}
  .bb-sub strong{color:#e8edf5;font-weight:500}
  .bb-cta{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}
  .bb-bm{display:inline-flex;align-items:center;gap:8px;padding:14px 32px;
    border-radius:6px;background:#9966ff;color:#fff;font-family:'DM Mono',monospace;
    font-size:11px;font-weight:500;letter-spacing:.5px;text-decoration:none;
    transition:opacity .2s,transform .2s}
  .bb-bm:hover{opacity:.84;transform:translateY(-1px)}
  .bb-bg{display:inline-flex;align-items:center;gap:8px;padding:14px 32px;
    border-radius:6px;border:1px solid #243548;color:#b8c8dc;
    font-family:'DM Mono',monospace;font-size:11px;letter-spacing:.4px;
    text-decoration:none;transition:border-color .2s,color .2s;background:transparent}
  .bb-bg:hover{border-color:#9966ff;color:#9966ff}
  .bb-caveat{font-family:'DM Mono',monospace;font-size:9px;color:#6a7d95;
    text-align:center;margin-top:16px;letter-spacing:.3px;line-height:1.7}
  .bb-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:72px 0 24px}
  .bb-shead{margin-bottom:48px}
  .bb-pdt{margin-top:32px;background:rgba(255,179,64,0.07);
    border:1px solid rgba(255,179,64,.12);border-radius:12px;padding:28px 32px}
  .bb-pdt-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:20px}
  .bb-pdt-cell{background:#08101e;border:1px solid #1e2d42;border-radius:8px;padding:18px 20px}
  .bb-bcta{text-align:center;padding:100px 32px;border-top:1px solid #1e2d42;
    border-bottom:1px solid #1e2d42;margin-bottom:72px;position:relative;overflow:hidden}
  .bb-bcta h2{font-family:'DM Mono',monospace;font-weight:500;
    font-size:clamp(24px,3.2vw,42px);margin-bottom:16px;color:#e8edf5}
  .bb-bcta p{font-family:'DM Mono',monospace;font-size:12px;color:#b8c8dc;
    margin:0 auto 40px;max-width:440px;line-height:1.92;font-weight:300}
  .bb-demo{display:inline-flex;align-items:center;gap:14px;padding:14px 24px;
    flex-wrap:wrap;background:rgba(153,102,255,.07);border:1px solid rgba(153,102,255,.18);
    border-radius:8px;font-family:'DM Mono',monospace;font-size:10px;color:#b8c8dc;margin-top:20px}
  .bb-risk{background:#0c1525;border:1px solid #1e2d42;border-radius:10px;
    padding:24px 28px;margin-bottom:56px}
  .bb-footer{display:flex;justify-content:space-between;align-items:center;
    padding:28px 0;border-top:1px solid #1e2d42;font-family:'DM Mono',monospace;
    font-size:9px;color:#6a7d95;flex-wrap:wrap;gap:12px}
  .bb-footer-logo{font-family:'DM Mono',monospace;font-weight:500;font-size:14px;color:#e8edf5}
  @media(max-width:900px){
    .bb-nav{padding:14px 20px}
    .bb-nav-links{display:none}
    .bb-stats{grid-template-columns:repeat(2,1fr)}
    .bb-pdt-grid{grid-template-columns:1fr}
    .bb-page{padding:0 20px 72px}
    .bb-hero{padding:80px 0 72px}
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// INVESTOR HOME
// ─────────────────────────────────────────────────────────────────────────────
export default function InvestorHome() {
  const [mounted, setMounted] = useState(false);
  const [mkt, setMkt] = useState(getMarketStatus());

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    const i = setInterval(() => setMkt(getMarketStatus()), 30000);
    return () => { clearTimeout(t); clearInterval(i); };
  }, []);

  const reveal = (d = 0) => ({
    opacity: mounted ? 1 : 0,
    animation: mounted ? `fadeUp .8s ${d}s ease both` : "none",
  });

  return (
    <div style={{ background:C.bg, color:C.text, minHeight:"100vh",
      overflowX:"hidden", fontFamily:"'DM Mono',monospace" }}>
      <style>{CSS}</style>

      {/* ── NAV ── */}
      <nav className="bb-nav">
        <a className="bb-logo" href="/">
          <div className="bb-mark">B</div>
          BerBul AI Trading
        </a>
        <ul className="bb-nav-links">
          <li><a href="#traction">Traction</a></li>
          <li><a href="#architecture">Architecture</a></li>
          <li><a href="#scale">Scale</a></li>
          <li><a href="#defensibility">Defensibility</a></li>
        </ul>
        <div style={{ display:"flex", alignItems:"center" }}>
          <Link className="bb-btn-ghost" to="/Login">Sign In</Link>
          <a className="bb-btn-solid" href="mailto:demo@berbul.ai">Request Demo →</a>
        </div>
      </nav>

      <div style={{ paddingTop:57 }}>
        <MarketBar />
      </div>
      <Ticker />

      <div className="bb-page bb-root">

        {/* ═══════════════════════════════════════════════════════════
            HERO
        ═══════════════════════════════════════════════════════════ */}
        <section className="bb-hero">
          <div className="bb-glow" />
          <div className="bb-badge" style={reveal(.1)}>
            <Orb color={C.purple} size={6} />
            INVESTOR OVERVIEW · SERIES SEED · SPY 0DTE
          </div>
          <div className="bb-h1" style={reveal(.2)}>
            <span className="bb-h1-hook">Institutional-grade algorithmic trading.</span>
            <span style={{ display:"block", fontSize:"clamp(32px,5vw,64px)", color:C.green,
              fontWeight:700, letterSpacing:"-1px", lineHeight:1.1, marginBottom:10 }}>
              Consumer scale.
            </span>
            <span className="bb-h1-frame">
              The decision architecture quant funds spend millions to build —
              running on $20 a month.
            </span>
          </div>
          <p className="bb-sub" style={reveal(.38)}>
            BerBul is a <strong>fully autonomous AI trading agent</strong> for SPY 0DTE options.
            Five AI layers. No human in the loop. Every decision logged in plain English.
            Proven across 47 paper-trading sessions with a{" "}
            <strong>78% win rate and $487 average daily return</strong> on fixed capital.
          </p>
          <div className="bb-cta" style={reveal(.52)}>
            <a className="bb-bm" href="mailto:demo@berbul.ai">Request Demo Access</a>
            <Link className="bb-bg" to="/">View Consumer Product →</Link>
          </div>
          <div className="bb-caveat" style={reveal(.6)}>
            Paper trading · Nov 2024 – present · full session logs available on request
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            THESIS
        ═══════════════════════════════════════════════════════════ */}
        <ThesisSection />

        {/* ═══════════════════════════════════════════════════════════
            TRACTION
        ═══════════════════════════════════════════════════════════ */}
        <div id="traction" style={{ paddingTop:80 }}>
          <div className="bb-shead" style={{ textAlign:"center" }}>
            <Eyebrow>Traction</Eyebrow>
            <Heading style={{ textAlign:"center" }}>Signal proven. Architecture validated.</Heading>
            <Body style={{ textAlign:"center", maxWidth:480, margin:"0 auto" }}>
              47 sessions of paper-trading data across diverse market conditions —
              trending, sideways, volatile, and event-driven.
            </Body>
          </div>
          <div className="bb-stats">
            {VC_STATS.map((s, i) => (
              <StatCard key={i} {...s} delay={i * .07} />
            ))}
          </div>
          <div className="bb-caveat" style={{ marginBottom:72 }}>
            Paper account · SPY 0DTE · Nov 2024–present ·
            past performance does not guarantee future results
          </div>
          <div style={{ marginBottom:8 }}>
            <Eyebrow>Session History Sample</Eyebrow>
            <Heading style={{ marginBottom:8 }}>Four conditions. Four outcomes.</Heading>
            <Body style={{ maxWidth:580, marginBottom:0 }}>
              Including a loss day — because the moat is in how the system
              handles every condition, not just favourable ones.
            </Body>
          </div>
          <PerfTable />
        </div>

        <Divider />

        {/* ═══════════════════════════════════════════════════════════
            ARCHITECTURE
        ═══════════════════════════════════════════════════════════ */}
        <section id="architecture">
          <div style={{ background:C.cDim, border:`1px solid ${C.cyan}18`,
            borderRadius:12, padding:"36px 40px", marginBottom:72, textAlign:"center" }}>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
              color:C.cyan, letterSpacing:"2px", marginBottom:16 }}>
              FULL TRANSPARENCY
            </div>
            <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
              fontSize:"clamp(18px,2.4vw,28px)", color:C.text, lineHeight:1.3, marginBottom:16 }}>
              Every other trading AI is a black box.<br />
              <span style={{ color:C.cyan }}>BerBul is the opposite.</span>
            </div>
            <Body style={{ maxWidth:520, margin:"0 auto" }}>
              Every decision has a reason. Every reason is readable. The full audit trail —
              entry logic, monitoring checks, exit conditions — is available after every session.
            </Body>
          </div>
          <div className="bb-shead">
            <Eyebrow>The AI Architecture</Eyebrow>
            <Heading>Five layers. One coherent intelligence.</Heading>
            <Body style={{ maxWidth:560 }}>
              Each layer has a distinct role. Together they form an autonomous trading system
              that predicts, validates, decides, monitors, and learns — without human intervention at any step.
            </Body>
          </div>
          <Architecture />
        </section>

        <Divider />

        {/* ═══════════════════════════════════════════════════════════
            CAPITAL MODEL
        ═══════════════════════════════════════════════════════════ */}
        <section>
          <div className="bb-shead">
            <Eyebrow>The Capital Model</Eyebrow>
            <Heading>Fixed capital. Daily cycle. Zero overnight risk.</Heading>
            <Body style={{ maxWidth:520 }}>
              The same capital pool recycles every session — no swing trades, no overnight
              exposure, no black swan risk while users sleep. The edge is frequency and discipline, not leverage.
            </Body>
          </div>
          <div className="bb-pdt">
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <span style={{ fontSize:14, color:C.amber }}>⚡</span>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
                color:C.amber, letterSpacing:"1.2px", fontWeight:500 }}>
                PATTERN DAY TRADER — WHAT IT MEANS FOR USERS
              </span>
            </div>
            <Body>
              Trading SPY multiple times per day triggers the SEC's Pattern Day Trader rule,
              requiring $25,000 minimum account equity. Here is what the actual risk exposure looks like.
            </Body>
            <div className="bb-pdt-grid">
              {[
                { label:"ACCOUNT MINIMUM",   val:"$25,000",  sub:"PDT regulatory requirement", color:C.amber },
                { label:"MAX LIVE EXPOSURE", val:"< $4,000", sub:"at any single moment",        color:C.green },
                { label:"CAPITAL AT RISK",   val:"~15%",     sub:"of minimum account size",     color:C.cyan  },
              ].map(({ label, val, sub, color }) => (
                <div key={label} className="bb-pdt-cell">
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:8,
                    color:C.meta, letterSpacing:"1.1px", marginBottom:8 }}>{label}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontWeight:500,
                    fontSize:26, color, marginBottom:6, letterSpacing:"-.3px" }}>{val}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:C.body }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Divider />

        {/* ═══════════════════════════════════════════════════════════
            SCALE
        ═══════════════════════════════════════════════════════════ */}
        <section id="scale">
          <div className="bb-shead">
            <Eyebrow>The Scale Story</Eyebrow>
            <Heading>Proven on SPY. Built to scale.</Heading>
            <Body style={{ maxWidth:560 }}>
              The underlying models are instrument-agnostic. Historical data and sufficient
              liquidity are the only requirements to expand. SPY has effectively infinite
              liquidity at current position sizes — scaling from 10 contracts to 100 moves no markets.
            </Body>
          </div>
          <Roadmap />
        </section>

        <Divider />

        {/* ═══════════════════════════════════════════════════════════
            DEFENSIBILITY + COST
        ═══════════════════════════════════════════════════════════ */}
        <section id="defensibility">
          <div className="bb-shead">
            <Eyebrow>The Defensibility</Eyebrow>
            <Heading>Why this compounds with time.</Heading>
            <Body style={{ maxWidth:520 }}>
              A competitor starting today cannot replicate this in six months. Each of the
              four properties below widens the moat with every session that runs.
            </Body>
          </div>
          <MoatSection />

          <div style={{ height:64 }} />

          <div className="bb-shead">
            <Eyebrow>The Cost Structure</Eyebrow>
            <Heading>Built on institutional architecture. Not institutional budget.</Heading>
            <Body style={{ maxWidth:580 }}>
              Trained models, RL validation, autonomous agents, reactive monitoring —
              running on $20 a month. The edge was never the money. It was the access.
              That barrier is gone.
            </Body>
          </div>
          <CostStructure />
        </section>

        <Divider />

        {/* ═══════════════════════════════════════════════════════════
            CATEGORY COMPARISON
        ═══════════════════════════════════════════════════════════ */}
        <section id="comparison" style={{ paddingBottom:0 }}>
          <div className="bb-shead">
            <Eyebrow>What Category Is This?</Eyebrow>
            <Heading>Not a better trading tool.<br />A different kind of product.</Heading>
            <Body style={{ maxWidth:580 }}>
              Signal generators, copy trading, and education platforms all assume you want
              to become a better trader. BerBul's premise is different.
            </Body>
          </div>
          <CategoryComparison />
        </section>

        <Divider />

        {/* ═══════════════════════════════════════════════════════════
            CTA
        ═══════════════════════════════════════════════════════════ */}
        <section>
          <section className="bb-bcta">
            <h2>Request demo access.</h2>
            <p>
              Full platform · live paper account · complete session logs ·
              no NDA required. We'll walk you through the full system.
            </p>
            <div className="bb-cta">
              <a className="bb-bm" href="mailto:demo@berbul.ai">demo@berbul.ai</a>
              <Link className="bb-bg" to="/">View Consumer Product →</Link>
            </div>
            <div className="bb-demo">
              <Orb color={C.purple} size={6} />
              <span>Questions before reaching out?</span>
              <Link to="/approach" style={{ color:C.purple, textDecoration:"none", fontWeight:500 }}>
                Read the full approach →
              </Link>
            </div>
          </section>
          <RiskDisclosure />
        </section>

        {/* ── FOOTER ── */}
        <footer className="bb-footer">
          <span className="bb-footer-logo">BerBul</span>
          <span>SPY · 0DTE · AUTONOMOUS EXECUTION · EST. 2026</span>
          <a href="mailto:demo@berbul.ai"
            style={{ color:C.meta, textDecoration:"none", transition:"color .2s" }}
            onMouseEnter={e => e.target.style.color = C.purple}
            onMouseLeave={e => e.target.style.color = C.meta}>
            demo@berbul.ai
          </a>
        </footer>

      </div>
    </div>
  );
}